<template>
  <v-card>
    <v-card-title class="pb-0 mb-0">
      <div class="card-title">
        <span class="bold table-card-title">
          {{ title }}
        </span>

        <div class="status-search-group">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" :value="itemsFilter.map((i) => i.value)" @input="getFilterResult" />
          <headers-data-table-settings :headers.sync="headers" :default-headers="getDefaultHeadersOptions()" />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar banners" />
          </div>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        class="history-striped"
        :headers="computedHeaders"
        :search="search"
        :query="query"
        :query-variables="queryVariables"
        :custom-items-per-page="franchisee ? 5 : 20"
        :items-per-page-options="[5, 10, 15, 20]"
        :graphql-uri="$microservicesUrls['fedGateway']"
        :default-table-sort-by="defaultTableSortBy"
        :default-table-sort-desc="defaultTableSortDesc"
        :reset-to-default-sort-when-empty="resetToDefaultSortWhenEmpty"
        query-name="commercials"
      >
        <template v-slot:item.title="{ item }" style="max-width: 300px">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-tooltip v-model="showTooltipTitle[item.id]" max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <router-link v-if="!franchisee" class="link" :to="getEditRoute(item._id, item.status)" v-on="on">
                    {{ item.title | truncate({ length: nameTruncate }) }}
                  </router-link>
                  <span v-else v-on="on">{{ item.title | truncate({ length: nameTruncate }) }}</span>
                </template>
                <span>{{ item.title }}</span>
              </mf-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.description="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip v-model="showTooltipDescription[item._id]" max-width="250" top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ getFormatedText(item.description) }}</span>
                </template>
                <span>{{ item.description | hifen }}</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.units="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" style="cursor: auto" v-on="on">
                    {{ getUnitsLabel(item, true) }}
                  </span>
                </template>
                <span v-for="(unit, index) in getFilteredUnits(item)" :key="index" style="display: block">
                  {{ unit.name }}
                </span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.priority="{ item }">
          <div class="d-flex align-center colorful-border">
            <div class="py-0 px-4 d-flex align-center">
              {{ getPriority(item.priority) }}
            </div>
          </div>
        </template>

        <template v-slot:item.validity="{ item }">
          <div class="d-flex align-center colorful-border">
            <div class="py-0 px-4 d-flex align-center">
              {{
                item.initial_date && item.final_date
                  ? `${DateTime.fromISO(item.initial_date).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(item.final_date).toFormat('dd/MM/yyyy')}`
                  : '-'
              }}
            </div>
          </div>
        </template>

        <template v-slot:item.action.type="{ item }">
          <div class="d-flex align-center colorful-border">
            <div class="py-0 px-4 d-flex align-center">
              <span v-if="franchisee" class="no-link-table">
                {{
                  item.action && item.action.type === 'VIEW_SALES'
                    ? 'Oferta'
                    : item.action && item.action.type === 'VIEW_FLYER'
                    ? 'Encarte'
                    : item.action && item.action.type === 'EXTERNAL_LINK'
                    ? 'Link externo'
                    : item.action && item.action.type === 'INTERNAL_PAGE'
                    ? 'Link interno'
                    : 'Sem link'
                }}
              </span>
              <template v-else>
                <span v-if="item.action && item.action.type === 'VIEW_SALES'" class="link-table" @click="handleRedirect(item)">Visualizar oferta</span>
                <span v-else-if="item.action && item.action.type === 'VIEW_FLYER'" class="link-table" @click="handleRedirect(item)">Visualizar encarte</span>
                <a
                  v-else-if="item.action && item.action.type === 'EXTERNAL_LINK' && item.action.link"
                  :href="item.action.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="link-table"
                >
                  Link externo
                </a>
                <span v-else-if="item.action && item.action.type === 'INTERNAL_PAGE'" class="no-link-table"> Link interno </span>
                <span v-else class="no-link-table">Sem link</span>
              </template>
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-icon fill small :color="getStatusLabel(item.status).color">fiber_manual_record</mf-icon>
              {{ getStatusLabel(item.status).text }}
            </div>
          </div>
        </template>

        <template v-slot:item.updated_at="{ item }">
          <div class="d-flex align-center colorful-border">
            <div class="py-0 px-4 d-flex align-center">
              {{ item.updated_at ? `${DateTime.fromISO(item.updated_at).toFormat('dd/MM/yyyy')}` : '-' }}
            </div>
          </div>
        </template>

        <template v-slot:item.menu="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <div v-if="!franchisee">
                <v-menu class="campaigns-table-actions" bottom origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on }">
                    <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <v-list-item @click="redirectToEdit(item._id)">
                      <v-list-item-title>{{ item.status === 'FINISHED' ? 'Visualizar' : 'Editar' }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'SCHEDULED'" @click="unscheduleCommercial(item._id, item.title)">
                      <v-list-item-title>Suspender</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'DRAFT'" data-cy="delete-highlight" @click="deleteCommercial(item._id, item.title)">
                      <v-list-item-title>Deletar</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="cloneCommercial(item._id, item.title)">
                      <v-list-item-title>Duplicar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'ACTIVE'" @click="finalizeCommercial(item._id, item.title)">
                      <v-list-item-title>Finalizar</v-list-item-title>
                    </v-list-item>
                    <!-- TODO: Adicionar COMMERCIAL na query de operationLogs para filtrar os logs do banner-->
                    <!-- <v-list-item @click="openLogsModal(item.id)">
                      <v-list-item-title>Logs</v-list-item-title>
                    </v-list-item> -->
                    <v-list-item v-if="hasResults(item.status)" @click="redirectToResults(item._id)">
                      <v-list-item-title>Resultados</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </template>
      </paginated-data-table>
    </v-card-text>
    <!-- <logs-modal v-model="logsDialog" tags="COMMERCIAL" logTitle="Banners" :idItem="bannerId" /> -->
  </v-card>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'

import { DateTime } from 'luxon'
import { getStatusLabel } from '@/config/helpers.js'

import {
  QUERY_GET_COMERCIALS,
  MUTATION_UNSCHEDULED_COMMERCIAL,
  MUTATION_FINALIZE_COMMERCIAL,
  MUTATION_CLONE_COMMERCIAL,
  MUTATION_DELETE_COMMERCIAL,
  QUERY_FLYER_STATUS,
  QUERY_SALES_CAMPAIGN_STATUS,
} from '../../graphql'

import truncate from 'lodash/truncate'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'CampaignsTable',
  components: {
    PaginatedDataTable,
    HeadersDataTableSettings,
  },
  filters: {
    hifen(value) {
      if (!value) return '-'
      return value
    },
  },
  props: {
    title: String,
    franchisee: Boolean,
    units: Array,
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    partnerUnits: Array,
    products: {
      type: Array,
      default: () => [],
    },
    itemsFilter: {
      type: Array,
      default: () => [],
    },
    defaultTableSortBy: {
      type: [String, Array],
      default: '',
    },
    defaultTableSortDesc: {
      type: [Boolean, Array],
      default: false,
    },
    resetToDefaultSortWhenEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    bannerId: '',
    logsDialog: false,
    showTooltipTitle: {},
    showTooltipDescription: {},
    DateTime,
    headers: [],
    statusToSearch: [],
  }),
  computed: {
    query() {
      return QUERY_GET_COMERCIALS
    },
    computedHeaders() {
      return this.headers
        .filter((h) => h.isActive)
        .map((h) => ({
          ...h,
          width: h.width,
          sortable: h.hasOwnProperty('sortable') ? h.sortable : true,
        }))
    },
    parsedStatus() {
      return this.statusToSearch.length ? this.statusToSearch : []
    },
    queryVariables() {
      const status = this.statusToSearch.length ? this.statusToSearch : this.itemsFilter.map((i) => i.value)

      return {
        mode: 'BANNER',
        status,
        franchise: this.franchisee,
        only_franchise: this.franchisee || this.isFranchise,
        ...(this.units?.length
          ? {
              units: this.units.map((u) => u.id_erp),
              units_configuration: this.unitsConfiguration,
              partner_units: this.partnerUnits,
            }
          : {}),
      }
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise || false
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee || false
    },
    nameTruncate() {
      return this.industry ? 62 : 18
    },
  },
  watch: {
    itemsFilter: {
      handler(newValue) {
        this.statusToSearch = newValue.map((item) => item.value)
      },
      immediate: true,
    },
  },
  beforeMount() {
    this.statusToSearch = Array.isArray(this.itemsFilter) && this.itemsFilter.length ? this.itemsFilter.map((item) => item.value) : []
    if (this.headers.length === 0) {
      this.headers = cloneDeep(this.getDefaultHeadersOptions())
    }
  },
  methods: {
    getStatusLabel,
    getFilteredUnits(item) {
      if (!item.units?.length || !this.units?.length) return []

      const filteredErps = this.units.map((u) => u.id_erp)
      return item.units.filter((u) => filteredErps.includes(u.id_erp))
    },
    getUnitsLabel(item, withLimit = true) {
      const filtered = this.getFilteredUnits(item)
      if (!filtered.length) return '-'
      if (withLimit && filtered.length > 1) return `${filtered.length} lojas`
      return filtered[0].name || '-'
    },
    handleRedirect(item) {
      if (!item?.action?.type) return
      const { type, reference_id } = item.action
      if (type === 'VIEW_FLYER') {
        this.redirectToFlyer(reference_id)
      } else if (type === 'VIEW_SALES') {
        this.redirectToSales(reference_id)
      }
    },
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    openLogsModal(id) {
      this.bannerId = id
      this.logsDialog = true
    },
    getEditRoute(id, status) {
      return status === 'FINISHED' ? { name: 'banners-result', params: { id }, query: { franchise: this.franchise } } : { name: 'banners-edit', params: { id } }
    },
    redirectToEdit(id) {
      const route = this.getEditRoute(id)
      this.$router.push(route).catch(() => {})
    },
    getResultsRoute(id) {
      return { name: 'banners-result', params: { id }, query: { franchise: this.franchise } }
    },
    redirectToResults(id) {
      const route = this.getResultsRoute(id)
      this.$router.push(route).catch(() => {})
    },
    unit(item) {
      if (!item.units || !item.units.length) return '-'
      const matchedNames = item.units
        .map((unitIdErp) => {
          const fullUnit = this.units.find((u) => u.id_erp === unitIdErp)
          return fullUnit?.name || null
        })
        .filter(Boolean)
      return matchedNames.length >= 2 ? `${matchedNames.length} lojas` : matchedNames[0] || '-'
    },
    getFormatedText(text) {
      return truncate(text || '', {
        length: 24,
        separator: ' ',
      })
    },
    getPriority(priority) {
      return { 3: 'Alta', 2: 'Média', 1: 'Baixa' }[priority] || '-'
    },
    async redirectToFlyer(id) {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_FLYER_STATUS,
          variables: {
            _id: id,
            franchise: this.franchise,
            only_franchise: this.franchise,
          },
          fetchPolicy: 'no-cache',
        })

        const flyer = data?.flyer
        if (!flyer?.status) throw new Error('Flyer inválido')

        const isFinished = flyer.status.toLowerCase() === 'finished'
        const route = isFinished ? { name: 'encartes-resultados', params: { id } } : { name: 'encartes-edit', params: { id } }

        const href = this.$router.resolve(route).href
        window.open(href, '_blank')
      } catch (error) {
        console.error(error)
        this.$snackbar('Erro ao buscar dados do encarte.')
      }
    },

    async redirectToSales(id) {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_SALES_CAMPAIGN_STATUS,
          variables: {
            id,
            franchise: this.franchise,
          },
          fetchPolicy: 'no-cache',
        })

        const campaign = data?.offerSchedule
        if (!campaign?.status) throw new Error('Campanha inválida')

        const isFinished = campaign.status.toLowerCase() === 'finished'
        const route = isFinished
          ? {
              name: 'gestao-ofertas-resultado',
              params: { id },
              query: { franchise: String(this.franchise) },
            }
          : { name: 'gestao-ofertas-editar', params: { id } }

        const href = this.$router.resolve(route).href
        window.open(href, '_blank')
      } catch (error) {
        console.error(error)
        this.$snackbar('Erro ao buscar dados da campanha de ofertas.')
      }
    },
    async generateModalInfos(name, model) {
      const parsedName = name ? name.trim().bold() : ''
      const title = {
        clone: 'Duplicar banner',
        delete: 'Remover banner',
        unschedule: 'Suspender banner',
        finish: 'Finalizar banner',
      }
      const message = {
        clone: `Tem certeza que deseja duplicar o banner ${parsedName}?`,
        delete: `Tem certeza que deseja excluir o banner ${parsedName}?`,
        unschedule: `O banner ${parsedName} será movido para o status de Rascunho`,
        finish: `O banner ${parsedName} será movido para o status concluído`,
      }
      const confirmText = {
        clone: 'Duplicar',
        delete: 'Remover',
        unschedule: 'Ok',
        finish: 'Ok',
      }
      try {
        await this.$confirm({
          title: title[model],
          message: message[model],
          confirmText: confirmText[model],
          cancelText: 'Cancelar',
        })
        return true
      } catch {
        return false
      }
    },
    async cloneCommercial(id, name) {
      const confirmed = await this.generateModalInfos(name, 'clone')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: MUTATION_CLONE_COMMERCIAL,
          variables: { id },
        })
        const _id = data?.cloneCommercial?._id
        if (_id) this.$router.push({ name: 'banners-edit', params: { id: _id } }).catch(() => {})
      } catch {
        this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
      } finally {
        this.$refs.table.paginationLoading = false
      }
    },
    async deleteCommercial(id, name) {
      const confirmed = await this.generateModalInfos(name, 'delete')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_DELETE_COMMERCIAL,
          variables: { id },
        })
        this.$refs.table.waitAndGetData()
      } catch {
        this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
      } finally {
        this.$refs.table.paginationLoading = false
      }
    },
    async unscheduleCommercial(id, name) {
      const confirmed = await this.generateModalInfos(name, 'unschedule')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_UNSCHEDULED_COMMERCIAL,
          variables: { id },
        })
        this.$refs.table.waitAndGetData()
      } catch {
        this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
      } finally {
        this.$refs.table.paginationLoading = false
      }
    },
    async finalizeCommercial(id, name) {
      const confirmed = await this.generateModalInfos(name, 'finish')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_FINALIZE_COMMERCIAL,
          variables: { id },
        })
        this.$store.commit('smsCountsChanged', true)
        this.$refs.table.waitAndGetData()
      } catch {
        this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
      } finally {
        this.$refs.table.paginationLoading = false
      }
    },
    hasResults(status) {
      return ['ACTIVE', 'FINISHED'].includes(status)
    },
    getMessageDates(date, time = null) {
      const messageData = time ? `${date} ${time}` : date
      const format = time ? 'DD/MM/YYYY | HH:mm' : 'DD/MM/YYYY'
      return date ? DateTime.fromISO(messageData).toFormat(format) : '-'
    },
    getDefaultHeadersOptions() {
      return [
        { text: 'Título', value: 'title', width: '24%', isActive: true, isDefault: true },
        { text: 'Descrição', value: 'description', width: '22%', isActive: true, isDefault: true },
        { text: 'Lojas', value: 'units', width: '10%', isActive: true, isDefault: true, sortable: false },
        { text: 'Prioridade', value: 'priority', width: '8%', isActive: true, isDefault: true },
        { text: 'Data de validade', value: 'validity', width: '12%', isActive: true, isDefault: true },
        { text: 'Redirecionamento', value: 'action.type', width: '6%', isActive: false, isDefault: false },
        { text: 'Status', value: 'status', width: '7%', isActive: true, isDefault: true },
        { text: 'Última atualização', value: 'updated_at', width: '11%', isActive: true, isDefault: true },
        { text: '', value: 'menu', width: '5%', isActive: true, isDefault: true, sortable: false },
      ]
    },
  },
}
</script>
<style lang="scss" scoped>
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.link-table {
  font-size: 12px;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
}

.no-link-table {
  font-size: 12px;
  color: #666;
}

.min-width-345 {
  min-width: 345px;
}

.status-search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.history-striped {
  tr:nth-child(even) {
    background-color: #f7f7f7;
  }
}

.text-align-right {
  text-align: right;
  margin-right: 12px;
}

.colorful-border {
  border-left: 3px solid;
  height: 100%;
}

tr td:not(:first-child) .colorful-border {
  border: none;
}

::v-deep td {
  padding: 0 !important;
}

.margin-left-btn {
  margin-left: 12px;
}

.primary-color {
  background-color: #007bff;
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: 0.5px;
}

.fix-td-position {
  padding: 0 16px;
  display: flex;
  align-items: center;
}

.modality-chip {
  width: 95px;
  text-align: center;
  display: flex;
  justify-content: center;
}

@media screen and (max-width: 1366px) {
  .link {
    font-size: 12px !important;
  }
}
</style>
